import React, { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Slider, Typography } from '@mui/material';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { api, type DemandSupplyResponse, type MasterToolProjectResult } from '../../services/api';
import { DEMAND_SUPPLY_COLORS } from '../map';
import type { SelectedDemandSupplyTypes } from '../../../types';
import { RADIUS_MAX_KM, RADIUS_STEP_KM } from './mapResearchConfig';

/**
 * Super Ratio comparison — one bar per compare-cart project for every period,
 * HDB upgrader demand above the zero baseline and competitive pool below it.
 */

type ChartView = 'yearly' | 'monthly' | 'cumulative';

const VIEWS: Array<{ id: ChartView; label: string }> = [
  { id: 'yearly', label: 'Year' },
  { id: 'monthly', label: 'Month' },
  { id: 'cumulative', label: 'Cumulative' },
];

/** Per-project hues; demand uses the solid tone, pool the muted one. */
const SERIES_COLORS = [
  { demand: '#22d3ee', supply: '#0e7490' },
  { demand: '#a78bfa', supply: '#6d28d9' },
  { demand: '#fbbf24', supply: '#b45309' },
];

const CYAN = '#22d3ee';

/** Matches the Super Ratio tab and Super Map: Condo/EC starts off, the rest on. */
const DEFAULT_TYPES: SelectedDemandSupplyTypes = {
  hdbBtoMop: true,
  hdbResaleMop: true,
  condoSsd: false,
  newLaunch: true,
  gls: true,
};

/**
 * The bars are coloured per project here, so the toggles carry the canonical
 * per-type hues instead — same vocabulary as the single-project panel and the
 * Super Ratio map markers.
 */
interface TypeChip {
  key: keyof SelectedDemandSupplyTypes;
  label: string;
  color: string;
  shape: 'circle' | 'square' | 'diamond';
}

const UPGRADER_CHIPS: TypeChip[] = [
  { key: 'hdbBtoMop', label: 'BTO', color: DEMAND_SUPPLY_COLORS.dark.hdbBto, shape: 'circle' },
  { key: 'hdbResaleMop', label: 'Resale', color: DEMAND_SUPPLY_COLORS.dark.hdbResale, shape: 'circle' },
];

const COMPETITIVE_CHIPS: TypeChip[] = [
  { key: 'condoSsd', label: 'Condo/EC', color: DEMAND_SUPPLY_COLORS.dark.condo, shape: 'circle' },
  { key: 'newLaunch', label: 'New launch', color: DEMAND_SUPPLY_COLORS.dark.newLaunch, shape: 'square' },
  { key: 'gls', label: 'GLS', color: DEMAND_SUPPLY_COLORS.dark.gls, shape: 'diamond' },
];
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const microLabelSx = {
  fontFamily: '"SF Mono", "Roboto Mono", Menlo, monospace',
  fontSize: 9.5,
  letterSpacing: '0.14em',
  textTransform: 'uppercase' as const,
  color: 'rgba(148, 197, 222, 0.75)',
};

const formatMonth = (month: string): string => {
  const [y, m] = month.split('-');
  return `${MONTH_NAMES[parseInt(m, 10) - 1]} ${y}`;
};

const formatUnits = (value: number): string =>
  value >= 10000 ? `${(value / 1000).toFixed(1)}k` : Math.round(value).toLocaleString();

interface CompareSuperRatioChartProps {
  projects: Array<MasterToolProjectResult & { id: string }>;
  /** Outer edge of the scan ring in meters — seeded from the Super Map radius. */
  radiusMeters?: number;
  /** Inner edge of the scan ring in meters; 0 scans from each project outwards. */
  minRadiusMeters?: number;
}

interface SeriesEntry {
  key: string;
  name: string;
  colors: { demand: string; supply: string };
  data: DemandSupplyResponse | null;
}

const CompareSuperRatioChart: React.FC<CompareSuperRatioChartProps> = ({
  projects,
  radiusMeters = 2000,
  minRadiusMeters = 0,
}) => {
  const [view, setView] = useState<ChartView>('yearly');
  // One selection for every project in the cart — the bars only stay comparable
  // if each project is measured from the same components.
  const [selectedTypes, setSelectedTypes] = useState<SelectedDemandSupplyTypes>(DEFAULT_TYPES);
  const [dataById, setDataById] = useState<Record<string, DemandSupplyResponse>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The slider drags as an [inner, outer] km band; the refetch waits for release
  // so dragging across the range doesn't fire one request per project per step.
  const [rangeKm, setRangeKm] = useState<[number, number]>([
    minRadiusMeters / 1000,
    radiusMeters / 1000,
  ]);
  const [appliedRangeKm, setAppliedRangeKm] = useState<[number, number]>([
    minRadiusMeters / 1000,
    radiusMeters / 1000,
  ]);
  useEffect(() => {
    const next: [number, number] = [minRadiusMeters / 1000, radiusMeters / 1000];
    setRangeKm(next);
    setAppliedRangeKm(next);
  }, [minRadiusMeters, radiusMeters]);
  const [innerKm, outerKm] = rangeKm;
  const [appliedInnerKm, appliedOuterKm] = appliedRangeKm;

  const projectKey = projects.map((p) => p.id).join('|');

  useEffect(() => {
    if (projects.length === 0) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const results = await Promise.all(
          projects.map(async (project) => {
            try {
              const response = await api.getDemandSupply(
                project.id,
                Math.round(appliedOuterKm * 1000),
                Math.round(appliedInnerKm * 1000),
              );
              return [project.id, response] as const;
            } catch {
              return [project.id, null] as const;
            }
          }),
        );
        if (cancelled) return;
        const next: Record<string, DemandSupplyResponse> = {};
        let anyLoaded = false;
        for (const [id, response] of results) {
          if (response) {
            next[id] = response;
            anyLoaded = true;
          }
        }
        setDataById(next);
        if (!anyLoaded) setError('Super Ratio data is unavailable for these projects.');
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load Super Ratio data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectKey, appliedInnerKm, appliedOuterKm]);

  const series = useMemo<SeriesEntry[]>(
    () =>
      projects.map((project, index) => ({
        key: `p${index}`,
        name: project.name?.trim() || 'Unnamed',
        colors: SERIES_COLORS[index % SERIES_COLORS.length],
        data: dataById[project.id] ?? null,
      })),
    [projects, dataById],
  );

  const chartData = useMemo(() => {
    const rows = new Map<string, Record<string, number | string>>();
    const cumulative: Record<string, { demand: number; supply: number }> = {};

    for (const entry of series) {
      if (!entry.data) continue;
      cumulative[entry.key] = { demand: 0, supply: 0 };
      for (const point of entry.data.timeline) {
        const bucket = view === 'yearly' ? point.month.slice(0, 4) : point.month;
        const row = rows.get(bucket) ?? { bucket };
        const demand =
          (selectedTypes.hdbBtoMop ? point.hdbBtoMop : 0) +
          (selectedTypes.hdbResaleMop ? point.hdbResaleMop : 0);
        const supply =
          (selectedTypes.condoSsd ? point.condoSsd : 0) +
          (selectedTypes.newLaunch ? point.newLaunch : 0) +
          (selectedTypes.gls ? point.gls : 0);

        if (view === 'cumulative') {
          cumulative[entry.key].demand += demand;
          cumulative[entry.key].supply += supply;
          row[`${entry.key}_demand`] = Math.round(cumulative[entry.key].demand);
          row[`${entry.key}_supplyNeg`] = -Math.round(cumulative[entry.key].supply);
        } else {
          const prevDemand = Number(row[`${entry.key}_demand`] ?? 0);
          const prevSupply = Number(row[`${entry.key}_supplyNeg`] ?? 0);
          row[`${entry.key}_demand`] = Math.round(prevDemand + demand);
          row[`${entry.key}_supplyNeg`] = Math.round(prevSupply - supply);
        }
        rows.set(bucket, row);
      }
    }

    return Array.from(rows.values()).sort((a, b) => String(a.bucket).localeCompare(String(b.bucket)));
  }, [series, view, selectedTypes]);

  // Upgrader and pool share one step so the two directions stay comparable, but
  // each side is bounded by its own data — no empty 10k/15k gridlines above a 5k bar.
  const { yDomain, yTicks } = useMemo<{ yDomain: [number, number]; yTicks: number[] }>(() => {
    let maxUp = 0;
    let maxDown = 0;
    for (const row of chartData) {
      for (const [key, raw] of Object.entries(row)) {
        if (key === 'bucket') continue;
        const value = Number(raw) || 0;
        if (value > 0) maxUp = Math.max(maxUp, value);
        else maxDown = Math.max(maxDown, -value);
      }
    }
    if (maxUp <= 0 && maxDown <= 0) return { yDomain: [-100, 100], yTicks: [-100, 0, 100] };
    const rough = Math.max(maxUp, maxDown) / 3;
    const pow = Math.pow(10, Math.floor(Math.log10(rough)));
    const n = rough / pow;
    const step = (n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10) * pow;
    const stepsUp = Math.max(maxUp > 0 ? 1 : 0, Math.ceil(maxUp / step));
    const stepsDown = Math.max(maxDown > 0 ? 1 : 0, Math.ceil(maxDown / step));
    const ticks: number[] = [];
    for (let i = -stepsDown; i <= stepsUp; i += 1) ticks.push(i * step);
    return { yDomain: [-stepsDown * step, stepsUp * step], yTicks: ticks };
  }, [chartData]);

  const yearTicks = useMemo(
    () =>
      view === 'yearly'
        ? undefined
        : chartData.map((row) => String(row.bucket)).filter((bucket) => bucket.endsWith('-01')),
    [chartData, view],
  );

  const toggleType = (key: keyof SelectedDemandSupplyTypes) =>
    setSelectedTypes((prev) => ({ ...prev, [key]: !prev[key] }));

  // Compact toggle pill. Shapes match the Super Ratio map markers
  // (circle / square / diamond); on = tinted + solid, off = dimmed + hollow.
  const typeChip = (chip: TypeChip) => {
    const on = selectedTypes[chip.key];
    return (
      <Box
        key={chip.key}
        role="button"
        aria-pressed={on}
        onClick={() => toggleType(chip.key)}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.6,
          pl: 0.75,
          pr: 1,
          py: 0.35,
          borderRadius: 999,
          cursor: 'pointer',
          userSelect: 'none',
          border: '1px solid',
          borderColor: on ? chip.color : 'rgba(148, 197, 222, 0.35)',
          bgcolor: on ? `color-mix(in srgb, ${chip.color} 18%, transparent)` : 'transparent',
          opacity: on ? 1 : 0.55,
          transition: 'opacity 0.12s ease, border-color 0.12s ease, background-color 0.12s ease',
          '&:hover': { opacity: 1, borderColor: chip.color },
        }}
      >
        <Box
          sx={{
            width: 9,
            height: 9,
            flexShrink: 0,
            borderRadius: chip.shape === 'circle' ? '50%' : chip.shape === 'square' ? '2px' : 0,
            transform: chip.shape === 'diamond' ? 'rotate(45deg)' : 'none',
            bgcolor: on ? chip.color : 'transparent',
            border: on ? 'none' : `1.5px solid ${chip.color}`,
          }}
        />
        <Typography
          sx={{ fontSize: 11.5, color: '#eef6fa', fontWeight: on ? 700 : 400, whiteSpace: 'nowrap' }}
        >
          {chip.label}
        </Typography>
      </Box>
    );
  };

  // One labelled group of toggles. Upgrader sits above the chart and Competitive
  // below it, so each legend hugs the bars it controls — as in the project panel.
  const chipRow = (label: string, chips: TypeChip[], spacing: { mt?: number; mb?: number }) => (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 0.75,
        ...spacing,
        px: 1.25,
        py: 1,
        border: '1px solid rgba(34, 211, 238, 0.22)',
        borderRadius: '10px',
        bgcolor: 'rgba(8, 18, 28, 0.35)',
      }}
    >
      <Typography sx={{ ...microLabelSx, color: 'rgba(196, 220, 233, 0.9)', mr: 0.25 }}>{label}</Typography>
      {chips.map(typeChip)}
    </Box>
  );

  if (projects.length === 0) {
    return (
      <Box
        sx={{
          py: 5,
          textAlign: 'center',
          border: '1px dashed rgba(34, 211, 238, 0.25)',
          borderRadius: '12px',
          bgcolor: 'rgba(34, 211, 238, 0.04)',
        }}
      >
        <Typography sx={{ fontSize: 14, color: 'rgba(222, 238, 246, 0.75)' }}>
          Add projects to the compare cart to see their Super Ratio.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Scan ring — drag both ends for a from/to band such as 2–5 km */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 1.25,
          px: 1.75,
          pt: 1,
          pb: 2.25,
          border: '1px solid rgba(34, 211, 238, 0.22)',
          borderRadius: '10px',
          bgcolor: 'rgba(8, 18, 28, 0.35)',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
          <Typography sx={microLabelSx}>Scan Radius</Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#a78bfa', whiteSpace: 'nowrap' }}>
            {innerKm > 0 ? `${innerKm.toFixed(1)}–${outerKm.toFixed(1)} km` : `${outerKm.toFixed(1)} km`}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, minWidth: 120, pr: 1 }}>
          <Slider
            size="small"
            value={rangeKm}
            min={0}
            max={RADIUS_MAX_KM}
            step={RADIUS_STEP_KM}
            disabled={loading}
            disableSwap
            onChange={(_e, value) => {
              const [lo, hi] = value as number[];
              // Keep at least one step of band so the ring never collapses.
              if (hi - lo < RADIUS_STEP_KM) return;
              setRangeKm([lo, hi]);
            }}
            onChangeCommitted={(_e, value) => {
              const [lo, hi] = value as number[];
              setAppliedRangeKm([lo, hi]);
            }}
            marks={[0, 2, 5, 10].map((v) => ({ value: v, label: `${v}` }))}
            sx={{
              color: '#8b5cf6',
              py: 0.5,
              '& .MuiSlider-thumb': { boxShadow: '0 0 8px rgba(139, 92, 246, 0.8)' },
              '& .MuiSlider-markLabel': {
                color: 'rgba(148, 197, 222, 0.75)',
                fontSize: 10,
                fontFamily: '"SF Mono", "Roboto Mono", Menlo, monospace',
                top: 18,
              },
            }}
          />
        </Box>
      </Box>

      {/* Project legend + view toggle */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1,
          mb: 1.25,
          px: 1.25,
          py: 1,
          border: '1px solid rgba(34, 211, 238, 0.22)',
          borderRadius: '10px',
          bgcolor: 'rgba(8, 18, 28, 0.35)',
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.25, flex: 1, minWidth: 0 }}>
          {series.map((entry) => (
            <Box key={entry.key} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  flexShrink: 0,
                  borderRadius: '3px',
                  background: `linear-gradient(180deg, ${entry.colors.demand} 0%, ${entry.colors.supply} 100%)`,
                  border: '1px solid rgba(255,255,255,0.35)',
                }}
              />
              <Typography
                sx={{
                  fontSize: 11.5,
                  fontWeight: 700,
                  color: entry.data ? '#eef6fa' : 'rgba(196, 220, 233, 0.45)',
                  lineHeight: 1.3,
                }}
              >
                {entry.name}
                {!entry.data && !loading ? ' · n/a' : ''}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
          {VIEWS.map((item) => {
            const active = view === item.id;
            return (
              <Box
                key={item.id}
                component="button"
                type="button"
                onClick={() => setView(item.id)}
                sx={{
                  appearance: 'none',
                  cursor: 'pointer',
                  px: 1,
                  py: 0.5,
                  borderRadius: '8px',
                  fontFamily: '"SF Mono", "Roboto Mono", Menlo, monospace',
                  fontSize: 10,
                  fontWeight: 800,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: active ? '#04121d' : 'rgba(196, 220, 233, 0.75)',
                  bgcolor: active ? CYAN : 'transparent',
                  border: `1px solid ${active ? CYAN : 'rgba(34, 211, 238, 0.35)'}`,
                  transition: 'background-color 0.15s ease, color 0.15s ease',
                  '&:hover': { bgcolor: active ? '#67e8f9' : 'rgba(34, 211, 238, 0.12)' },
                }}
              >
                {item.label}
              </Box>
            );
          })}
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.25, py: 6 }}>
          <CircularProgress size={26} sx={{ color: CYAN }} />
          <Typography sx={{ ...microLabelSx, color: CYAN }}>Loading Super Ratio…</Typography>
        </Box>
      ) : error ? (
        <Typography sx={{ py: 4, textAlign: 'center', fontSize: 13, color: '#f87171', fontWeight: 600 }}>
          {error}
        </Typography>
      ) : (
        <>
          {/* Upgrader toggles sit above the chart, over the upward bars they label */}
          {chipRow('Upgrader ▲', UPGRADER_CHIPS, { mb: 1.25 })}

          <Box sx={{ width: '100%', height: { xs: 300, sm: 340 } }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={chartData}
                margin={{ top: 16, right: 12, bottom: 8, left: 4 }}
                barCategoryGap="24%"
                barGap={2}
                stackOffset="sign"
              >
                <CartesianGrid stroke="rgba(34, 211, 238, 0.12)" vertical={false} />
                <XAxis
                  dataKey="bucket"
                  ticks={yearTicks}
                  tickFormatter={(value: string) => (view === 'yearly' ? value : value.slice(0, 4))}
                  tick={{ fill: 'rgba(148, 197, 222, 0.8)', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  width={46}
                  domain={yDomain}
                  ticks={yTicks}
                  tickFormatter={(value: number) => formatUnits(Math.abs(value))}
                  tick={{ fill: 'rgba(148, 197, 222, 0.8)', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <ReferenceLine y={0} stroke="rgba(196, 220, 233, 0.5)" strokeWidth={1.5} />
                <Tooltip
                  cursor={{ fill: 'rgba(34, 211, 238, 0.08)' }}
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    const row = payload[0].payload as Record<string, number | string>;
                    return (
                      <Box
                        sx={{
                          bgcolor: 'rgba(8, 18, 30, 0.96)',
                          border: '1px solid rgba(34, 211, 238, 0.35)',
                          borderRadius: '10px',
                          px: 1.5,
                          py: 1,
                          boxShadow: '0 10px 28px rgba(2, 8, 20, 0.55)',
                        }}
                      >
                        <Typography sx={{ ...microLabelSx, color: CYAN, mb: 0.5 }}>
                          {view === 'yearly' ? String(label) : formatMonth(String(label))}
                        </Typography>
                        {series.map((entry) => {
                          const demand = Number(row[`${entry.key}_demand`] ?? 0);
                          const supply = Math.abs(Number(row[`${entry.key}_supplyNeg`] ?? 0));
                          if (!entry.data) return null;
                          return (
                            <Box key={entry.key} sx={{ mt: 0.5 }}>
                              <Typography
                                sx={{ fontSize: 11.5, fontWeight: 800, color: entry.colors.demand, lineHeight: 1.3 }}
                              >
                                {entry.name}
                              </Typography>
                              <Typography sx={{ fontSize: 11, color: '#eef6fa', lineHeight: 1.4 }}>
                                Upgrader {demand.toLocaleString()} · Competitive {supply.toLocaleString()}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    );
                  }}
                />
                {view === 'yearly'
                  ? series.flatMap((entry) => [
                      <Bar
                        key={`${entry.key}-demand`}
                        dataKey={`${entry.key}_demand`}
                        stackId={entry.key}
                        name={entry.name}
                        fill={entry.colors.demand}
                        fillOpacity={0.9}
                        radius={[4, 4, 0, 0]}
                        isAnimationActive={false}
                      />,
                      <Bar
                        key={`${entry.key}-supply`}
                        dataKey={`${entry.key}_supplyNeg`}
                        stackId={entry.key}
                        name={`${entry.name} pool`}
                        fill={entry.colors.supply}
                        fillOpacity={0.9}
                        radius={[0, 0, 4, 4]}
                        isAnimationActive={false}
                      />,
                    ])
                  : series.flatMap((entry) => [
                      <Line
                        key={`${entry.key}-demand`}
                        type="monotone"
                        dataKey={`${entry.key}_demand`}
                        name={entry.name}
                        stroke={entry.colors.demand}
                        strokeWidth={2}
                        dot={false}
                        isAnimationActive={false}
                      />,
                      <Line
                        key={`${entry.key}-supply`}
                        type="monotone"
                        dataKey={`${entry.key}_supplyNeg`}
                        name={`${entry.name} pool`}
                        stroke={entry.colors.supply}
                        strokeWidth={2}
                        strokeDasharray="5 4"
                        dot={false}
                        isAnimationActive={false}
                      />,
                    ])}
              </ComposedChart>
            </ResponsiveContainer>
          </Box>

          {/* Competitive toggles mirror the downward bars they control */}
          {chipRow('Competitive ▼', COMPETITIVE_CHIPS, { mt: 1 })}

          <Typography
            sx={{
              display: 'block',
              mt: 1.25,
              fontSize: 11,
              color: 'rgba(196, 220, 233, 0.65)',
              lineHeight: 1.5,
            }}
          >
            Potential HDB Upgrader (above the line): HDB blocks reaching 5-yr MOP (completion + 5, spread across the MOP year) and
            resale HDB purchases reaching 5-yr MOP from transaction date. Competitive Pool (below the line): private units
            past SSD (3 yrs if bought before 4 Jul 2025, 4 yrs after; EC new sales ≈ 8 yrs), unsold new launch units
            (from today), and awarded GLS units (12 months after award).
          </Typography>
        </>
      )}
    </Box>
  );
};

export default CompareSuperRatioChart;
